import React, { useState } from 'react';
import {
    VStack,
    HStack,
    Button,
    Text,
    Box,
    Heading,
    Radio,
    RadioGroup,
    Progress,
    Stack,
} from '@chakra-ui/react';
import InfoMessage from './shared/InfoMessage';

/**
 * SurveyPreview - Renders survey exactly as respondents will see it
 * Supports both old question format and new page-based format with text blocks
 */
function SurveyPreview({ isOpen, onClose, poll, questions }) {
    const [currentPageIndex, setCurrentPageIndex] = useState(-1); // -1 = prologue
    const [responses, setResponses] = useState({});

    // Don't render if not open
    if (!isOpen) return null;

    // Detect if we're receiving survey items (new format) or questions (old format)
    const isSurveyItemsFormat = questions && questions.length > 0 && questions[0].item_type;

    /**
     * Group survey items into pages based on page breaks
     * For old format, each question is its own "page"
     */
    const getPages = () => {
        if (!questions || questions.length === 0) return [];

        if (isSurveyItemsFormat) {
            // New format: group by page breaks
            const pages = [];
            let currentPage = [];

            questions.forEach((item) => {
                if (item.item_type === 'page_break') {
                    if (currentPage.length > 0) {
                        pages.push(currentPage);
                        currentPage = [];
                    }
                } else {
                    currentPage.push(item);
                }
            });

            if (currentPage.length > 0) {
                pages.push(currentPage);
            }

            return pages;
        } else {
            // Old format: each question is its own page
            return questions.map(q => [{
                item_type: 'question',
                question_data: q
            }]);
        }
    };

    const pages = getPages();
    const totalPages = pages.length;

    const getCurrentPageQuestions = () => {
        if (currentPageIndex < 0 || currentPageIndex >= pages.length) return [];
        return pages[currentPageIndex].filter(item => item.item_type === 'question');
    };

    const isCurrentPageComplete = () => {
        const pageQuestions = getCurrentPageQuestions();
        if (pageQuestions.length === 0) return true;
        return pageQuestions.every(item =>
            responses[item.question_data.id] !== undefined
        );
    };

    const handleChoiceSelect = (questionId, choiceId) => {
        setResponses(prev => ({
            ...prev,
            [questionId]: choiceId,
        }));
    };

    const handleNextPage = () => {
        if (currentPageIndex === -1) {
            setCurrentPageIndex(0);
        } else if (currentPageIndex < pages.length - 1) {
            setCurrentPageIndex(prev => prev + 1);
        } else {
            // Survey complete (preview mode)
            handleReset();
        }
    };

    const handleReset = () => {
        setCurrentPageIndex(-1);
        setResponses({});
    };

    const progressPercent = currentPageIndex === -1 ? 0 : ((currentPageIndex + 1) / totalPages) * 100;

    if (!questions || questions.length === 0) {
        return (
            <Box p={8} textAlign="center">
                <Text color="gray.500">
                    No questions available to preview. Add questions to see the survey experience.
                </Text>
            </Box>
        );
    }

    // Prologue view
    if (currentPageIndex === -1) {
        return (
            <VStack spacing={6} align="stretch">
                <Box p={8} bg="gray.50" borderRadius="lg">
                    <Heading as="h2" size="lg" mb={6} textAlign="center">
                        Survey Introduction
                    </Heading>
                    <Text fontSize="md" mb={6} whiteSpace="pre-wrap">
                        {poll?.prologue || ''}
                    </Text>
                    <Button
                        onClick={handleNextPage}
                        colorScheme="teal"
                        size="lg"
                        width="full"
                    >
                        Begin Survey
                    </Button>
                </Box>
                <InfoMessage>
                    This is a preview. Respondents will see exactly this.
                </InfoMessage>
            </VStack>
        );
    }

    // Survey page view
    const currentPage = pages[currentPageIndex];

    return (
        <VStack spacing={6} align="stretch">
            <Progress
                value={progressPercent}
                size="sm"
                colorScheme="teal"
                borderRadius="full"
            />

            <Box>
                <Text fontSize="sm" color="gray.600" mb={2}>
                    Page {currentPageIndex + 1} of {totalPages}
                </Text>
            </Box>

            <Box p={6} bg="gray.50" borderRadius="lg">
                <VStack spacing={6} align="stretch">
                    {/* Render all items on current page */}
                    {currentPage.map((item, itemIndex) => {
                        if (item.item_type === 'text_block') {
                            return (
                                <Box key={`text-${itemIndex}`} mb={4}>
                                    <Text fontSize="md" whiteSpace="pre-wrap" fontWeight="medium">
                                        {item.text_content}
                                    </Text>
                                </Box>
                            );
                        }

                        if (item.item_type === 'question' && item.question_data) {
                            const question = item.question_data;
                            return (
                                <Box key={`question-${question.id}`} mb={6}>
                                    <Heading as="h3" size="md" mb={4}>
                                        {question.text}
                                    </Heading>

                                    <RadioGroup
                                        value={responses[question.id]?.toString() || ''}
                                        onChange={(value) => handleChoiceSelect(question.id, parseInt(value))}
                                    >
                                        <Stack spacing={3}>
                                            {question.choices && question.choices.map((choice) => (
                                                <Box
                                                    key={choice.id}
                                                    p={4}
                                                    borderRadius="md"
                                                    border="2px solid"
                                                    borderColor={responses[question.id] === choice.id ? 'teal.500' : 'gray.300'}
                                                    bg={responses[question.id] === choice.id ? 'rgba(0, 128, 128, 0.05)' : 'white'}
                                                    cursor="pointer"
                                                    transition="all 0.2s"
                                                    _hover={{ borderColor: 'teal.400' }}
                                                    onClick={() => handleChoiceSelect(question.id, choice.id)}
                                                >
                                                    <Radio value={choice.id.toString()} colorScheme="teal" pointerEvents="none">
                                                        <Text fontSize="md">{choice.text}</Text>
                                                    </Radio>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </RadioGroup>
                                </Box>
                            );
                        }

                        return null;
                    })}

                    <HStack justify="space-between" mt={4}>
                        <Button
                            onClick={handleReset}
                            variant="ghost"
                            size="sm"
                        >
                            Reset Preview
                        </Button>

                        <Button
                            onClick={handleNextPage}
                            colorScheme="teal"
                            size="lg"
                            isDisabled={!isCurrentPageComplete()}
                        >
                            {currentPageIndex === totalPages - 1 ? 'Submit (Preview)' : 'Next Page'}
                        </Button>
                    </HStack>
                </VStack>
            </Box>

            <InfoMessage>
                This is exactly what respondents will see. No responses will be recorded.
            </InfoMessage>
        </VStack>
    );
}

export default SurveyPreview;
